import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '../../components/Link';
import { Band } from '../../components/editorial/Band';
import { ProviderArtwork } from '../../components/ProviderArtwork';
import { DailyStack } from '../../components/charts/dailyTraffic/DailyStack';
import { buildDailyTraffic } from '../../components/charts/dailyTraffic/dailySeries';
import { catalogQueryOptions } from '../../features/catalog/queries';
import {
  findProviderProfile,
  type ProviderProfile,
} from '../../features/catalog/providerProfiles';
import { providerTrafficQueryOptions } from '../../features/catalog/providerTraffic';
import { groupCatalog } from '../../lib/catalogMap';
import { fmtReq } from '../../lib/format';
import { providerTrafficDays } from './providerTrafficSeries';
import { ProviderTapeTable } from './ProviderTapeTable';
import {
  buildProviderTapeRows,
  providerNetworkDays,
  sortProviderTapeRows,
  type ProviderTapeKey,
  type ProviderTapeSort,
} from './providerTape';

export interface ProviderProfilePageProps {
  providerId: string;
  initialProfile: ProviderProfile | null;
}

/** Descending first on every column except the ones where "best" is smallest. */
const firstDir = (key: ProviderTapeKey): ProviderTapeSort['dir'] =>
  key === 'rank' || key === 'name' || key === 'p50' || key === 'price' ? 'asc' : 'desc';

const dayLabel = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const NotFound = () => (
  <Band>
    <div className="band">
      <h1 className="display-2 text-text">Provider not found</h1>
      <p className="lede mt-4">This provider is no longer serving a public catalog endpoint.</p>
      <Link to="/providers" className="link-dotted mt-6 inline-flex min-h-10 items-center">
        Browse providers
      </Link>
    </div>
  </Band>
);

/**
 * One provider's public profile, drawn as the same tape as `/usage`: one large
 * number for what it served, its daily traffic stacked by platform, the
 * readings that qualify it, and every endpoint it stands behind in one ranked
 * table.
 *
 * No closing banner. A visitor here is auditing a number, and every row of the
 * table already sends them into the catalog.
 */
export const ProviderProfilePage = ({ providerId, initialProfile }: ProviderProfilePageProps) => {
  const entries = useQuery(catalogQueryOptions()).data;
  const profile = useMemo(
    () => (entries ? findProviderProfile(entries, providerId) : initialProfile),
    [entries, initialProfile, providerId],
  );
  const cards = useMemo(() => (entries ? groupCatalog(entries) : []), [entries]);
  const categories = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.id, card.category])),
    [cards],
  );
  const traffic = useQuery(providerTrafficQueryOptions(
    providerId,
    profile !== null && typeof window !== 'undefined',
  ));
  const [sort, setSort] = useState<ProviderTapeSort>({ key: 'requests', dir: 'desc' });

  const points = traffic.data?.points;
  const days = useMemo(() => (points ? providerTrafficDays(points) : []), [points]);
  const chart = useMemo(() => buildDailyTraffic(providerNetworkDays(days)), [days]);
  const rows = useMemo(
    () => (profile ? buildProviderTapeRows(profile.endpoints, points, cards) : []),
    [profile, points, cards],
  );
  const sorted = useMemo(() => sortProviderTapeRows(rows, sort), [rows, sort]);

  if (!profile) return <NotFound />;

  const total = rows.reduce((sum, row) => sum + row.requests, 0);
  const busiest = days.reduce<(typeof days)[number] | null>(
    (best, day) => (best === null || day.total > best.total ? day : best),
    null,
  );
  const readings = [
    { label: 'Active endpoints', value: fmtReq(rows.length) },
    { label: 'With traffic', value: fmtReq(rows.filter((row) => row.requests > 0).length) },
    { label: 'Platforms', value: fmtReq(new Set(rows.map((row) => row.platformId)).size) },
    {
      label: busiest && busiest.total > 0 ? `Busiest day, ${dayLabel(busiest.key)}` : 'Busiest day',
      value: busiest && busiest.total > 0 ? fmtReq(busiest.total) : '-',
    },
  ];

  const onSort = (key: ProviderTapeKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: firstDir(key) },
    );

  return (
    <section className="border-b border-rule">
      <div className="page-wrap">
        <div className="page-col">
          <header className="px-6 pt-12 pb-8 sm:px-8 lg:px-10">
            <div className="flex items-center gap-3">
              <ProviderArtwork source={profile.source} size={40} />
              <div className="mono text-11 uppercase tracking-widest text-text-3">
                <Link to="/providers" className="hover:text-text">Providers</Link>
                <span className="mx-2">/</span>
                <h1 className="inline text-text">{profile.source.name}</h1>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-3">
              <span className="mono text-6xl font-medium leading-none tracking-tight text-text s980:text-7xl">
                {total > 0 ? fmtReq(total) : '-'}
              </span>
              {/* The space is explicit because these are separate boxes, so the
                  text extraction does not read "17,272requests served". */}
              <span className="pb-1 text-16 text-text-2"> requests served in the last 30 days</span>
            </div>
            <p className="mt-4 max-w-160 text-15 leading-relaxed text-text-2">
              {profile.source.kind === 'brand'
                ? `${profile.source.name} is credited as the original data source for these endpoints.`
                : 'A stable public name that shows how this source performs without exposing private routing or account details.'}
            </p>
          </header>

          <div className="px-6 pt-4 pb-8 sm:px-8 lg:px-10">
            {traffic.isPending || traffic.isError || chart.readings.length === 0 ? (
              <p className="py-16 text-15 text-text-2">
                {traffic.isPending
                  ? 'Loading traffic...'
                  : traffic.isError
                    ? 'Traffic data is temporarily unavailable.'
                    : 'Nothing was served over this window.'}
              </p>
            ) : (
              <DailyStack traffic={chart} categories={categories} />
            )}
          </div>

          <dl className="grid grid-cols-2 divide-x divide-y divide-rule border-y border-rule s980:grid-cols-4 s980:divide-y-0">
            {readings.map((reading) => (
              <div key={reading.label} className="px-6 py-5">
                <dt className="mono text-10 uppercase tracking-widest text-text-3">{reading.label}</dt>
                <dd className="mono mt-2 text-22 leading-none text-text">{reading.value}</dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-wrap items-baseline justify-between gap-2 px-6 pt-10 pb-4 sm:px-8 lg:px-10">
            <h2 className="mono text-11 uppercase tracking-widest text-text-3">Every endpoint, ranked</h2>
            <span className="mono text-10 text-text-3 max-s720:hidden">Click a heading to re-rank</span>
          </div>

          <ProviderTapeTable rows={sorted} sort={sort} onSort={onSort} />

          <p className="px-6 pt-6 pb-12 text-12 leading-relaxed text-text-3 sm:px-8 lg:px-10">
            Requests are the calls this source served successfully over the last 30 days. Success
            and typical response are measured end to end on this source alone; price is what a
            customer pays when this source answers.
          </p>
        </div>
      </div>
    </section>
  );
};
